import { useState, useEffect } from 'react';

interface PWAInstallPrompt {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

interface PWACapabilities {
  isInstallable: boolean;
  isInstalled: boolean;
  isOnline: boolean;
  canShare: boolean;
  hasNotificationPermission: boolean;
  hasLocationPermission: boolean;
  supportsPushNotifications: boolean;
  supportsBackgroundSync: boolean;
}

export const usePWA = () => {
  const [installPrompt, setInstallPrompt] = useState<PWAInstallPrompt | null>(null);
  const [capabilities, setCapabilities] = useState<PWACapabilities>({
    isInstallable: false,
    isInstalled: false,
    isOnline: navigator.onLine,
    canShare: 'share' in navigator,
    hasNotificationPermission: false,
    hasLocationPermission: false,
    supportsPushNotifications: 'PushManager' in window,
    supportsBackgroundSync: 'serviceWorker' in navigator && 'sync' in window.ServiceWorkerRegistration.prototype
  });

  useEffect(() => {
    // Check if app is installed (running in standalone mode)
    const isInstalled = window.matchMedia('(display-mode: standalone)').matches ||
                       (window.navigator as any).standalone === true;

    // Listen for install prompt
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      setInstallPrompt(e as any);
      setCapabilities(prev => ({ ...prev, isInstallable: true }));
    };

    // Listen for app installed
    const handleAppInstalled = () => {
      setInstallPrompt(null);
      setCapabilities(prev => ({ 
        ...prev, 
        isInstalled: true, 
        isInstallable: false 
      }));
    };

    // Listen for online/offline status
    const handleOnline = () => {
      setCapabilities(prev => ({ ...prev, isOnline: true }));
    };

    const handleOffline = () => {
      setCapabilities(prev => ({ ...prev, isOnline: false }));
    };

    // Check permissions
    const checkPermissions = async () => {
      try {
        // Check notification permission
        const notificationPermission = await Notification.requestPermission();
        
        // Check location permission
        let locationPermission = false;
        if ('geolocation' in navigator) {
          try {
            const permission = await navigator.permissions.query({ name: 'geolocation' });
            locationPermission = permission.state === 'granted';
          } catch (error) {
            // Fallback for browsers that don't support permissions API
            locationPermission = false;
          }
        }

        setCapabilities(prev => ({
          ...prev,
          isInstalled,
          hasNotificationPermission: notificationPermission === 'granted',
          hasLocationPermission: locationPermission
        }));
      } catch (error) {
        console.error('Error checking permissions:', error);
      }
    };

    // Add event listeners
    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Initial permission check
    checkPermissions();

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const installApp = async () => {
    if (!installPrompt) return false;

    try {
      await installPrompt.prompt();
      const choiceResult = await installPrompt.userChoice;
      
      if (choiceResult.outcome === 'accepted') {
        setInstallPrompt(null);
        setCapabilities(prev => ({ 
          ...prev, 
          isInstallable: false,
          isInstalled: true 
        }));
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error installing app:', error);
      return false;
    }
  };

  const requestNotificationPermission = async () => {
    try {
      const permission = await Notification.requestPermission();
      const granted = permission === 'granted';
      
      setCapabilities(prev => ({ 
        ...prev, 
        hasNotificationPermission: granted 
      }));
      
      return granted;
    } catch (error) {
      console.error('Error requesting notification permission:', error);
      return false;
    }
  };

  const requestLocationPermission = async () => {
    return new Promise<boolean>((resolve) => {
      if (!('geolocation' in navigator)) {
        resolve(false);
        return;
      }

      navigator.geolocation.getCurrentPosition(
        () => {
          setCapabilities(prev => ({ 
            ...prev, 
            hasLocationPermission: true 
          }));
          resolve(true);
        },
        () => {
          resolve(false);
        },
        { timeout: 10000 }
      );
    });
  };

  const shareContent = async (data: ShareData) => {
    if (!capabilities.canShare) {
      // Fallback to clipboard
      try {
        await navigator.clipboard.writeText(data.url || data.text || '');
        return true;
      } catch (error) {
        return false;
      }
    }

    try {
      await navigator.share(data);
      return true;
    } catch (error) {
      console.error('Error sharing content:', error);
      return false;
    }
  };

  const registerForPushNotifications = async () => {
    if (!capabilities.supportsPushNotifications || !capabilities.hasNotificationPermission) {
      return null;
    }

    try {
      const registration = await navigator.serviceWorker.ready;
      const vapidKey = import.meta.env.VITE_VAPID_PUBLIC_KEY || 'demo-vapid-key';
      
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: vapidKey
      });

      return subscription;
    } catch (error) {
      console.error('Error registering for push notifications:', error);
      return null;
    }
  };

  const scheduleBackgroundSync = async (tag: string) => {
    if (!capabilities.supportsBackgroundSync) {
      return false;
    }

    try {
      const registration = await navigator.serviceWorker.ready;
      await registration.sync.register(tag);
      return true;
    } catch (error) {
      console.error('Error scheduling background sync:', error);
      return false;
    }
  };

  return {
    capabilities,
    installApp,
    requestNotificationPermission,
    requestLocationPermission,
    shareContent,
    registerForPushNotifications,
    scheduleBackgroundSync
  };
};